import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authHeaders } from '../../utils/api';
import { convertToCdnUrl } from '../../utils/cdnUtils';
import { compressImageFile } from '../../utils/imageCompressor';
import { lockScroll, unlockScroll } from '../../utils/scrollbarUtils';
import backIcon from '../../assets/icons/common/back_icon.png';
import adminReviewSettingIcon from '../../assets/icons/admin/review_setting_icon.png';
import adminHamburgerIcon from '../../assets/icons/admin/hamburger_icon.png';
import bottomNavBg from '../../assets/icons/common/bottomNav.png';

const ReviewSettings = ({ isOpen, onClose, onSave, storeId }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isReviewEnabled, setIsReviewEnabled] = useState(false);   // 네이버
  const [isKakaoEnabled, setIsKakaoEnabled] = useState(false);     // 카카오
  const [reviewNote, setReviewNote] = useState('');                // 참고사항
  const [naverPlaceId, setNaverPlaceId] = useState('');
  const [kakaoPlaceId, setKakaoPlaceId] = useState('');
  const [discountItems, setDiscountItems] = useState([]);
  const [, setValidationErrors] = useState({}); // eslint-disable-line no-unused-vars
  const [isClosing, setIsClosing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [, setDeletingItemId] = useState(null); // eslint-disable-line no-unused-vars

  // Tab state
  const [activeTab, setActiveTab] = useState('place');

  // Place ID state
  const [placeSearchQuery, setPlaceSearchQuery] = useState('');
  const [placeSearchResults, setPlaceSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [savedPlace, setSavedPlace] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [showPlaceToast, setShowPlaceToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' | 'warning'
  const [selectingPlaceIdx, setSelectingPlaceIdx] = useState(null);
  const [showSavedCard, setShowSavedCard] = useState(false);

  // Tab swipe
  const swipeStartX = useRef(null);
  const swipeStartY = useRef(null);

  const handleSwipeStart = (e) => {
    const touch = e.touches ? e.touches[0] : e;
    swipeStartX.current = touch.clientX;
    swipeStartY.current = touch.clientY;
  };

  const handleSwipeEnd = (e) => {
    if (swipeStartX.current === null) return;
    const touch = e.changedTouches ? e.changedTouches[0] : e;
    const diffX = touch.clientX - swipeStartX.current;
    const diffY = Math.abs(touch.clientY - swipeStartY.current);

    // 가로 이동이 50px 이상이고 세로보다 클 때만 탭 전환
    if (Math.abs(diffX) > 50 && Math.abs(diffX) > diffY) {
      if (diffX < 0 && activeTab === 'place') {
        setActiveTab('menu');
      } else if (diffX > 0 && activeTab === 'menu') {
        setActiveTab('place');
      }
    }
    swipeStartX.current = null;
    swipeStartY.current = null;
  };

  // Drag state
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const itemRefs = useRef([]);
  const formContainerRef = useRef(null);
  const imageInputRef = useRef(null);
  const [editingImageId, setEditingImageId] = useState(null);

  // Long press drag refs (즉시 참조)
  const dragState = useRef({
    isDragging: false,
    originalIndex: null,
    targetIndex: null,
    startY: 0,
    element: null,
    itemHeight: 100,
    longPressTimer: null,
  });

  const handleClose = useCallback(() => {
    // 우리가 push한 history 좀비 정리 (UI 버튼/하드웨어 백 동일 경로)
    if (window.history.state?.reviewSettings) {
      window.history.back();
      return;
    }
    // 먼저 슬라이드 아웃 애니메이션 시작
    setIsVisible(false);

    // 애니메이션이 완료된 후 상태 리셋 및 닫기
    setTimeout(() => {
      setIsAnimating(false);
      unlockScroll();
      onClose();
      // 페이지가 완전히 닫힌 후 상태 리셋
      setIsInitialized(false);
      setIsReviewEnabled(false);
      setDiscountItems([]);
    }, 300);
  }, [onClose]);

  // popstate listener에서 stale closure 방지 — handleClose ref로 우회
  const handleCloseRef = React.useRef(handleClose);
  React.useEffect(() => { handleCloseRef.current = handleClose; }, [handleClose]);

  // Discount item management
  const addDiscountItem = () => { // eslint-disable-line no-unused-vars
    const newItem = {
      id: Date.now(),
      value: '',
      enabled: true,  // 새 아이템은 기본적으로 활성화
      type: 'benefit',
      isFocused: false
    };
    setDiscountItems([...discountItems, newItem]);
  };

  const deleteDiscountItem = (id) => { // eslint-disable-line no-unused-vars
    // Start delete animation
    setDeletingItemId(id);
    
    // Wait for slide animation to complete, then remove item
    setTimeout(() => {
      setDiscountItems(discountItems.filter(item => item.id !== id));
      setDeletingItemId(null);
    }, 300);
  };

  const updateDiscountItem = (id, field, value) => {
    setDiscountItems(discountItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
    // Clear validation error when user starts typing
    if (field === 'value' && value) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`item-${id}`];
        return newErrors;
      });
    }
  };
  
  // Format number with comma
  const formatNumberWithComma = (value) => { // eslint-disable-line no-unused-vars
    const number = value.toString().replace(/[^0-9]/g, '');
    if (!number) return '';
    return parseInt(number).toLocaleString('ko-KR');
  };
  
  // Parse formatted number
  const parseFormattedNumber = (value) => {
    return value.toString().replace(/[^0-9]/g, '');
  };
  
  const handleDiscountChange = (id, value) => { // eslint-disable-line no-unused-vars
    const numericValue = parseFormattedNumber(value);
    updateDiscountItem(id, 'value', numericValue);
  };
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

  // === 리뷰 서비스 항목 (review_benefit_items — 전 POS 공용, 이름+이미지 직접 입력) ===
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');

  // 모달 표시 영역 — visualViewport 로 키보드 등장 시 보이는 영역 높이 추적.
  // overlay 의 height 를 그 값으로 잡으면 align-items:center 가 보이는 영역의 중앙에 모달 배치.
  const [wpVvHeight, setWpVvHeight] = useState(null);
  useEffect(() => {
    if (!isAddingItem || !window.visualViewport) return;
    const sync = () => setWpVvHeight(window.visualViewport.height);
    sync();
    window.visualViewport.addEventListener('resize', sync);
    return () => window.visualViewport.removeEventListener('resize', sync);
  }, [isAddingItem]);

  const handleAddBenefitItem = async () => {
    const trimmed = newItemName.trim();
    if (!trimmed) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/review-benefit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ store_id: storeId, name: trimmed }),
      });
      const d = await r.json();
      if (d.success) {
        setNewItemName('');
        setIsAddingItem(false);
        await reloadBenefitMenus();
      } else {
        alert(d.error || '등록 실패');
      }
    } catch (e) {
      console.error('[리뷰 서비스 등록] 실패:', e);
    }
  };

  const reloadBenefitMenus = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/stores/${storeId}/review-benefit-menus`);
      const d = await r.json();
      if (d.success) {
        setDiscountItems((d.menus || []).map(m => ({
          id: m.menu_key,
          menu_key: m.menu_key,
          menuId: m.menu_id || null,
          value: m.name || '',
          image_url: m.image_url || '',
          type: 'benefit',
          isFocused: false
        })));
      }
    } catch (e) {
      console.error('[리뷰 설정] 혜택 메뉴 로드 실패:', e);
    }
  };

  // 리뷰 서비스 항목 삭제 (S3 이미지도 함께 정리됨)
  const handleUnregisterBenefitMenu = async (menuKey) => {
    try {
      const url = `${API_BASE_URL}/api/review-benefit/${encodeURIComponent(menuKey)}`;
      await fetch(url, { method: 'DELETE', headers: authHeaders() });
      await reloadBenefitMenus();
    } catch (e) {
      console.error('[리뷰 설정] 메뉴 해제 실패:', e);
    }
  };

  // 매장명 검색
  const handlePlaceSearch = async () => {
    const input = placeSearchQuery.trim();
    if (!input) return;
    setIsSearching(true);
    setPlaceSearchResults([]);
    try {
      const response = await fetch(`${API_BASE_URL}/api/store-settings/naver-place/search?query=${encodeURIComponent(input)}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setPlaceSearchResults(data.items || []);
        if (data.items.length === 0) {
          alert('검색 결과가 없습니다.');
        }
      }
    } catch (error) {
      console.error('[매장 검색 오류]:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // 검색 결과 카드 클릭 → place ID 추출 → 등록
  const handlePlaceSelect = async (place, idx) => {
    setSelectingPlaceIdx(idx);
    try {
      // 1. 매장명 + 좌표로 place ID 추출
      const resolveResponse = await fetch(
        `${API_BASE_URL}/api/store-settings/naver-place/resolve-place?name=${encodeURIComponent(place.name)}&mapx=${place.mapx}&mapy=${place.mapy}`,
        { headers: authHeaders() }
      );
      const resolveData = await resolveResponse.json();

      if (!resolveData.success) {
        alert('매장 정보를 찾을 수 없습니다. 다시 시도해주세요.');
        return;
      }

      const placeId = resolveData.placeId;

      // 2. place ID로 상세 정보 조회
      const infoResponse = await fetch(`${API_BASE_URL}/api/store-settings/naver-place/${placeId}/info`, { headers: authHeaders() });
      const infoData = await infoResponse.json();

      if (!infoData.success) {
        alert('매장 상세 정보를 가져올 수 없습니다.');
        return;
      }

      // 3. DB에 저장
      const saveResponse = await fetch(`${API_BASE_URL}/api/store-settings/${storeId}/naver-place`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ placeId }),
      });
      const saveData = await saveResponse.json();

      if (saveData.success) {
        setSavedPlace(infoData.place);
        setPlaceSearchQuery('');
        setPlaceSearchResults([]);
        setLastCheckedAt(new Date());
        setToastType('success');
        setToastMessage('');
        setShowPlaceToast(true);
        setTimeout(() => setShowPlaceToast(false), 2000);
        setShowSavedCard(false);
        requestAnimationFrame(() => setShowSavedCard(true));
      }
    } catch (error) {
      console.error('[매장 등록 오류]:', error);
      alert('매장 등록 중 오류가 발생했습니다.');
    } finally {
      setSelectingPlaceIdx(null);
    }
  };

  const handlePlaceRefresh = async () => {
    if (!savedPlace?.placeId) return;
    setIsRefreshing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/store-settings/naver-place/${savedPlace.placeId}/info`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setSavedPlace(prev => ({
          ...prev,
          visitorReviewCount: data.place.visitorReviewCount,
        }));
        setLastCheckedAt(new Date());
      }
    } catch (error) {
      console.error('[플레이스 새로고침 오류]:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePlaceRemove = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/store-settings/${storeId}/naver-place`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ placeId: '' }),
      });
      const data = await response.json();
      if (data.success) {
        setSavedPlace(null);
        setShowSavedCard(false);

        // 매장 연결 해제 시 리뷰 토글도 자동 OFF (무효화)
        if (isReviewEnabled) {
          setIsReviewEnabled(false);
          try {
            await fetch(`${API_BASE_URL}/api/store-settings/${storeId}/review-settings`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({ isEnabled: false })
            });
            if (onSave) onSave();
          } catch (e) {
            console.error('[리뷰 토글 자동 OFF 실패]:', e);
          }
        }
      }
    } catch (error) {
      console.error('[플레이스 해제 오류]:', error);
    }
  };

  // 이미지 수정
  const handleImageEdit = (menuId) => {
    setEditingImageId(menuId);
    imageInputRef.current?.click();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingImageId) return;

    // 선택 즉시 업로드하는 경로라 미리보기가 없다 — 여기서 바로 압축한다.
    const compressed = await compressImageFile(file);

    const formData = new FormData();
    formData.append('image', compressed);

    const url = `${API_BASE_URL}/api/review-benefit/${editingImageId}/image`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        // Content-Type 은 지정하지 않는다 — FormData 는 브라우저가 boundary 를 붙여야 한다.
        headers: authHeaders(),
        body: formData,
      });
      const data = await response.json();
      if (data.success) {
        setDiscountItems(prev => prev.map(item =>
          item.menuId === editingImageId ? { ...item, image_url: data.imageUrl } : item
        ));
        if (onSave) onSave();
      }
    } catch (error) {
      console.error('[이미지 업로드 오류]:', error);
    } finally {
      setEditingImageId(null);
      e.target.value = '';
    }
  };

  // Long press drag handlers

  // === 드래그 순서 변경 (useMenuDragReorder 패턴 기반) ===
  const handleCardTouchStart = (e, index) => {
    if (dragState.current.isDragging) return;

    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    dragState.current.startY = clientY;
    dragState.current.startX = clientX;

    dragState.current.longPressTimer = setTimeout(() => {
      dragState.current.isDragging = true;
      dragState.current.originalIndex = index;
      dragState.current.targetIndex = index;
      dragState.current.element = itemRefs.current[index];

      // 카드 간 거리 = 카드 높이(offsetHeight) + CSS margin-bottom
      const el0 = itemRefs.current[0];
      if (el0) {
        const style = window.getComputedStyle(el0);
        dragState.current.itemHeight = el0.offsetHeight + parseFloat(style.marginBottom || 0);
      }

      if (navigator.vibrate) navigator.vibrate(30);
      setIsDraggingItem(true);

      // 탭 스크롤 잠금
      const tabPanel = itemRefs.current[index]?.closest('.tab-panel');
      if (tabPanel) tabPanel.style.overflow = 'hidden';

      // 드래그 시각 효과
      const el = dragState.current.element;
      if (el) {
        el.style.zIndex = '1000';
        el.style.position = 'relative';
        el.style.opacity = '0.95';
        el.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.2)';
        el.style.transition = 'none';
      }
    }, 600);
  };

  const handleCardTouchMove = (e) => {
    const ds = dragState.current;

    // long press 대기 중 움직임 감지 → 취소
    if (!ds.isDragging && ds.longPressTimer) {
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      if (Math.abs(clientY - ds.startY) > 10 || Math.abs(clientX - ds.startX) > 10) {
        clearTimeout(ds.longPressTimer);
        ds.longPressTimer = null;
      }
      return;
    }

    if (!ds.isDragging || !ds.element) return;

    e.preventDefault();
    e.stopPropagation();

    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = clientY - ds.startY;

    // 드래그 중인 카드 이동
    ds.element.style.transform = `translateY(${deltaY}px)`;
    ds.element.style.transition = 'none';

    // 현재 위치에서 target index 계산
    const dragCenterY = ds.element.getBoundingClientRect().top + ds.element.getBoundingClientRect().height / 2;
    let newTarget = ds.originalIndex;

    itemRefs.current.forEach((item, i) => {
      if (!item || i === ds.originalIndex) return;
      const rect = item.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;

      if (ds.originalIndex < i && dragCenterY > centerY) {
        newTarget = i;
      } else if (ds.originalIndex > i && dragCenterY < centerY) {
        if (newTarget === ds.originalIndex || i < newTarget) newTarget = i;
      }
    });

    ds.targetIndex = newTarget;

    // 다른 카드들 이동 애니메이션
    itemRefs.current.forEach((item, i) => {
      if (!item || i === ds.originalIndex) return;

      let ty = 0;
      if (ds.originalIndex < ds.targetIndex && i > ds.originalIndex && i <= ds.targetIndex) {
        ty = -ds.itemHeight;
      } else if (ds.originalIndex > ds.targetIndex && i >= ds.targetIndex && i < ds.originalIndex) {
        ty = ds.itemHeight;
      }

      item.style.transition = 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
      item.style.transform = `translateY(${ty}px)`;
    });
  };

  const handleCardTouchEnd = () => {
    const ds = dragState.current;

    // long press 타이머 정리
    if (ds.longPressTimer) {
      clearTimeout(ds.longPressTimer);
      ds.longPressTimer = null;
    }

    if (!ds.isDragging) return;

    // 순서 변경 적용
    if (ds.targetIndex !== ds.originalIndex) {
      const newItems = [...discountItems];
      const [moved] = newItems.splice(ds.originalIndex, 1);
      newItems.splice(ds.targetIndex, 0, moved);
      setDiscountItems(newItems);
      persistMenuOrder(newItems);
    }

    // 스타일 초기화
    itemRefs.current.forEach((item) => {
      if (!item) return;
      item.style.transition = 'none';
      item.style.transform = '';
      item.style.zIndex = '';
      item.style.position = '';
      item.style.opacity = '';
      item.style.boxShadow = '';
    });

    // 탭 스크롤 잠금 해제
    document.querySelectorAll('.tab-panel').forEach(el => {
      el.style.overflow = '';
    });

    // state/ref 리셋
    ds.isDragging = false;
    ds.originalIndex = null;
    ds.targetIndex = null;
    ds.element = null;
    setIsDraggingItem(false);
  };

  // 드래그 중 window 이벤트 리스너
  useEffect(() => {
    if (!isDraggingItem) return;

    const handleMove = (e) => handleCardTouchMove(e);
    const handleEnd = () => handleCardTouchEnd();

    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);

    return () => {
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
    };
  }, [isDraggingItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear refs when items change
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, discountItems.length);
  }, [discountItems]);

  // 경고 토스트 표시
  const showWarningToast = (message) => {
    setToastType('warning');
    setToastMessage(message);
    setShowPlaceToast(true);
    setTimeout(() => setShowPlaceToast(false), 2000);
  };

  // 네이버 리뷰 토글 (켜면 카카오 자동 off = 상호배타)
  const handleToggleEnabled = async () => {
    const newValue = !isReviewEnabled;
    if (newValue) {
      if (!naverPlaceId) { showWarningToast('마스터에서 네이버 Place ID를 먼저 설정해주세요'); return; }
      if (discountItems.length === 0) { showWarningToast('서비스 메뉴를 등록해주세요'); return; }
    }
    setIsReviewEnabled(newValue);
    if (newValue) setIsKakaoEnabled(false);
    try {
      await fetch(`${API_BASE_URL}/api/store-settings/${storeId}/review-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ isEnabled: newValue })
      });
      if (onSave) onSave();
    } catch (error) {
      console.error('[리뷰 설정] 네이버 토글 저장 실패:', error);
    }
  };

  // 카카오 리뷰 토글 (켜면 네이버 자동 off = 상호배타)
  const handleToggleKakao = async () => {
    const newValue = !isKakaoEnabled;
    if (newValue) {
      if (!kakaoPlaceId) { showWarningToast('마스터에서 카카오 Place ID를 먼저 설정해주세요'); return; }
      if (discountItems.length === 0) { showWarningToast('서비스 메뉴를 등록해주세요'); return; }
    }
    setIsKakaoEnabled(newValue);
    if (newValue) setIsReviewEnabled(false);
    try {
      await fetch(`${API_BASE_URL}/api/store-settings/${storeId}/kakao-review-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ isEnabled: newValue })
      });
      if (onSave) onSave();
    } catch (error) {
      console.error('[리뷰 설정] 카카오 토글 저장 실패:', error);
    }
  };

  // 참고사항 저장 (blur 시)
  const handleSaveNote = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/store-settings/${storeId}/review-note`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ note: reviewNote })
      });
    } catch (error) {
      console.error('[리뷰 설정] 참고사항 저장 실패:', error);
    }
  };

  // 순서 변경 — review_benefit_menus.display_order 를 PUT 으로 갱신.
  const persistMenuOrder = async (items) => {
    if (!storeId || !Array.isArray(items) || items.length === 0) return;
    try {
      await Promise.all(items.map((item, idx) => {
        if (!item.menuId) return null;
        return fetch(`${API_BASE_URL}/api/review-benefit/${encodeURIComponent(item.menuId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ display_order: idx + 1 }),
        });
      }));
    } catch (err) {
      console.error('[리뷰 메뉴 순서 저장 실패]', err);
    }
  };

  // Load settings only once when component first opens
  useEffect(() => {
    if (isOpen && !isInitialized && storeId) {
      const loadSettings = async () => {
        try {
          console.log('[리뷰 설정] 로드 시작, 매장 ID:', storeId);

          // 1. 리뷰 설정 (네이버/카카오 토글 + 참고사항) 로드
          const settingsResponse = await fetch(`${API_BASE_URL}/api/store-settings/${storeId}/review-settings`, { headers: authHeaders() });
          const settingsData = await settingsResponse.json();

          if (settingsData.success) {
            setIsReviewEnabled(settingsData.isEnabled || false);
            setIsKakaoEnabled(settingsData.kakaoEnabled || false);
            setReviewNote(settingsData.note || '');
            console.log('[리뷰 설정] ✅ 로드:', { naver: settingsData.isEnabled, kakao: settingsData.kakaoEnabled });
          }

          // 2. 등록된 리뷰 혜택 메뉴 로드
          await reloadBenefitMenus();

          // 3. Place ID 로드 (토글 검증용 — 연결은 마스터에서)
          try {
            const [nRes, kRes] = await Promise.all([
              fetch(`${API_BASE_URL}/api/store-settings/${storeId}/naver-place`, { headers: authHeaders() }),
              fetch(`${API_BASE_URL}/api/store-settings/${storeId}/kakao-place`, { headers: authHeaders() }),
            ]);
            const nData = await nRes.json().catch(() => ({}));
            const kData = await kRes.json().catch(() => ({}));
            if (nData.success) setNaverPlaceId(nData.placeId || '');
            if (kData.success) setKakaoPlaceId(kData.placeId || '');
          } catch (_) {}

        } catch (error) {
          console.error('[리뷰 설정] API 로드 실패:', error);
        } finally {
          setIsInitialized(true);
        }
      };

      loadSettings();
    }
  }, [isOpen, isInitialized, storeId]);

  // Handle page animation
  useEffect(() => {
    if (isOpen && !isClosing) {
      setIsAnimating(true);
      lockScroll(); // Lock background scroll when opening
      setTimeout(() => setIsVisible(true), 10);

      // pushState - isOpen 변경 시에만 실행
      window.history.pushState({ reviewSettings: true }, '');
    } else if (!isOpen && isAnimating && !isClosing) {
      setIsVisible(false);
      setTimeout(() => {
        setIsAnimating(false);
        unlockScroll(); // Unlock scroll after animation
        // Don't reset isInitialized here - keep the current state
      }, 300);
    }
  }, [isOpen, isClosing]); // eslint-disable-line react-hooks/exhaustive-deps

  // 뒤로가기 처리 - pushState와 분리
  // handleClose ref 사용으로 parent re-render churn에도 listener 1회만 등록
  useEffect(() => {
    if (!isOpen || isClosing) return;

    const handlePopState = () => {
      // 자기 history state가 top이면 자식 모달이 pop된 것 → 이름 입력 모달만 닫음
      if (window.history.state?.reviewSettings) {
        setIsAddingItem(false);
        return;
      }
      handleCloseRef.current?.();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      unlockScroll();
    };
  }, [isOpen, isClosing]);  // ← handleClose 의존 제거

  // Apply scale
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.review-settings-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    if (isAnimating) {
      scaleUI();
      window.addEventListener('resize', scaleUI);
      return () => window.removeEventListener('resize', scaleUI);
    }
  }, [isAnimating]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAnimating && !isOpen) return null;

  return (
    <>
      {/* Full Page */}
      <div className={`review-settings-page ${isVisible ? 'visible' : ''}`}>
        <div className="review-settings-wrapper">
          {/* Header */}
          <div className="page-header">
          <button className="back-button" onClick={handleClose}>
            <img 
              src={backIcon}
              alt="back"
              style={{ width: '27px', height: '27px' }}
            />
          </button>
          <div className="page-title-wrapper">
            <h1 className="page-title">리뷰 설정</h1>
          </div>
        </div>
        
        {/* Content — 단일 화면 (탭 제거, 매장연결은 마스터에서) */}
        <div className="page-content">
          <div className="tab-slider single">
          {/* (구 매장연결 탭 자리 — 비움) */}
            <div className="tab-panel" style={{ display: 'none' }}>
              {!isInitialized && (
                <div className="place-loading">불러오는 중...</div>
              )}
              {isInitialized && !savedPlace && (
                <>
                  <div className="place-section-title">네이버 매장 연결</div>
                  <div className="place-section-desc">
                    매장명을 검색하여 내 매장을 찾아주세요.
                  </div>

                  <div className="place-search-bar">
                    <input
                      type="text"
                      className="place-search-input"
                      placeholder="매장명 검색"
                      value={placeSearchQuery}
                      onChange={(e) => setPlaceSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); handlePlaceSearch(); } }}
                    />
                    <button className="place-search-btn" onClick={handlePlaceSearch} disabled={isSearching}>
                      {isSearching ? <span className="btn-spinner" /> : '검색'}
                    </button>
                  </div>

                  {placeSearchResults.length > 0 && (
                    <div className="place-results">
                      {placeSearchResults.map((place, idx) => (
                        <div
                          key={idx}
                          className={`place-card ${selectingPlaceIdx === idx ? 'selecting' : ''}`}
                          onClick={() => selectingPlaceIdx === null && handlePlaceSelect(place, idx)}
                        >
                          {selectingPlaceIdx === idx && <span className="card-spinner" />}
                          <div className="place-card-info">
                            <div className="place-card-name">{place.name}</div>
                            <div className="place-card-category">{place.category}</div>
                            <div className="place-card-address">{place.roadAddress || place.address}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {isInitialized && savedPlace && (
                <div className={`saved-place-card ${showSavedCard ? 'show' : ''}`}>
                  <div className="saved-place-name">
                    <span className="live-dot" />
                    <span className="saved-place-name-text">{savedPlace.name}</span>
                    <button className="place-disconnect-btn" onClick={handlePlaceRemove}>연결 해제</button>
                  </div>

                  <div className="stat-row">
                    <span className="stat-label">Place ID</span>
                    <span className="stat-value-id">{savedPlace.placeId}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">방문자 리뷰 수</span>
                    <span className="stat-value-review">{(savedPlace.visitorReviewCount || 0).toLocaleString()}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">마지막 조회</span>
                    <span className="stat-value-time">
                      {lastCheckedAt ? lastCheckedAt.toLocaleTimeString('ko-KR') : '-'}
                    </span>
                  </div>

                  <button
                    className="place-refresh-btn"
                    onClick={handlePlaceRefresh}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? '조회 중...' : '새로고침'}
                  </button>

                </div>
              )}
            </div>

          {/* Tab 2: Review Menu */}
            <div className="tab-panel">
              <div className="menu-toggle-card">
                <div className="menu-toggle-top">
                  <div className="menu-toggle-info">
                    <div className="menu-toggle-title">네이버 리뷰</div>
                    <div className="menu-toggle-desc">주문 완료 후 네이버 리뷰 작성을 유도합니다</div>
                  </div>
                  <button
                    className={`toggle-button ${isReviewEnabled ? 'active' : ''}`}
                    onClick={handleToggleEnabled}
                  >
                    <div className="toggle-slider" />
                  </button>
                </div>
                <div className="menu-toggle-divider" />
                <div className="menu-toggle-top">
                  <div className="menu-toggle-info">
                    <div className="menu-toggle-title">카카오 리뷰</div>
                    <div className="menu-toggle-desc">주문 완료 후 카카오 리뷰 작성을 유도합니다</div>
                  </div>
                  <button
                    className={`toggle-button toggle-kakao ${isKakaoEnabled ? 'active' : ''}`}
                    onClick={handleToggleKakao}
                  >
                    <div className="toggle-slider" />
                  </button>
                </div>
              </div>

              <div className="menu-list-card">
                <div className="menu-list-header">
                  <div className="menu-list-title2">리뷰 서비스 메뉴</div>
                  <div className="menu-list-count">{discountItems.length}개</div>
                </div>

                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />

                {discountItems.length === 0 && (
                  <div className="review-menu-add-row review-menu-add-row--empty">
                    <button
                      className="review-menu-add-card"
                      onClick={() => setIsAddingItem(true)}
                      aria-label="메뉴 추가"
                    >
                      <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4.5V17.5M4.5 11H17.5" stroke="#03C75A" strokeWidth="2.6" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                )}
                {discountItems.length > 0 && (
                  <div className="menu-list-items menu-list-items--with-add" ref={formContainerRef}>
                    {discountItems.map((item, index) => (
                      <div
                        key={item.id}
                        ref={el => itemRefs.current[index] = el}
                        className={`review-menu-card ${isDraggingItem && dragState.current.originalIndex === index ? 'dragging' : ''}`}
                        onTouchStart={(e) => handleCardTouchStart(e, index)}
                        onTouchEnd={handleCardTouchEnd}
                        onMouseDown={(e) => handleCardTouchStart(e, index)}
                        onMouseUp={handleCardTouchEnd}
                      >
                        <div className="review-menu-image-area">
                          {item.image_url ? (
                            <img className="review-menu-image" src={convertToCdnUrl(item.image_url)} alt={item.value} />
                          ) : (
                            <div className="review-menu-no-image">N</div>
                          )}
                        </div>
                        <div className="review-menu-info">
                          <div className="review-menu-name">
                            {item.value}
                          </div>
                          {item.menuId && (
                            <button
                              className="review-menu-edit-btn"
                              onClick={(e) => { e.stopPropagation(); handleImageEdit(item.menuId); }}
                            >
                              이미지 수정
                            </button>
                          )}
                        </div>
                        <button
                          className="review-menu-remove-btn"
                          onClick={(e) => { e.stopPropagation(); handleUnregisterBenefitMenu(item.menu_key); }}
                          aria-label="등록 해제"
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.93899 0.301937C1.72672 0.103217 1.44597 -0.00496705 1.15587 0.000175271C0.865778 0.0053176 0.588994 0.123385 0.383834 0.329505C0.178674 0.535624 0.0611564 0.813702 0.056038 1.10515C0.0509196 1.39661 0.1586 1.67868 0.356395 1.89194L4.4174 5.97194L0.356395 10.0519C0.24638 10.1549 0.158139 10.2791 0.0969375 10.4171C0.0357358 10.5551 0.00282704 10.7041 0.000174267 10.8552C-0.0024785 11.0062 0.0251791 11.1563 0.0814972 11.2963C0.137815 11.4364 0.22164 11.5637 0.327971 11.6705C0.434302 11.7773 0.56096 11.8615 0.70039 11.9181C0.83982 11.9747 0.989166 12.0025 1.13952 11.9998C1.28987 11.9972 1.43814 11.9641 1.5755 11.9026C1.71286 11.8411 1.83648 11.7525 1.93899 11.6419L6 7.56194L10.061 11.6419C10.1635 11.7525 10.2871 11.8411 10.4245 11.9026C10.5619 11.9641 10.7101 11.9972 10.8605 11.9998C11.0108 12.0025 11.1602 11.9747 11.2996 11.9181C11.439 11.8615 11.5657 11.7773 11.672 11.6705C11.7784 11.5637 11.8622 11.4364 11.9185 11.2963C11.9748 11.1563 12.0025 11.0062 11.9998 10.8552C11.9972 10.7041 11.9643 10.5551 11.9031 10.4171C11.8419 10.2791 11.7536 10.1549 11.6436 10.0519L7.5826 5.97194L11.6436 1.89194C11.8414 1.67868 11.9491 1.39661 11.944 1.10515C11.9388 0.813702 11.8213 0.535624 11.6162 0.329505C11.411 0.123385 11.1342 0.0053176 10.8441 0.000175271C10.554 -0.00496705 10.2733 0.103217 10.061 0.301937L6 4.38194L1.93899 0.301937Z" fill="#EC5A5A"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                    <div className="review-menu-add-row">
                      <button
                        className="review-menu-add-card"
                        onClick={() => setIsAddingItem(true)}
                        aria-label="메뉴 추가"
                      >
                        <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11 4.5V17.5M4.5 11H17.5" stroke="#03C75A" strokeWidth="2.6" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {discountItems.length > 0 && (
                  <div className="review-menu-hint">꾹 눌러서 순서를 변경하세요</div>
                )}
              </div>

              <div className="menu-list-card">
                <div className="menu-list-header">
                  <div className="menu-list-title2">참고사항</div>
                </div>
                <textarea
                  className="review-note-input"
                  placeholder="리뷰 선택 화면에 표시할 안내문 (예: 별 5개, 사진과 함께 3줄 이상)"
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  onBlur={handleSaveNote}
                  rows={3}
                />
              </div>

            </div>
          </div>
        </div>

          {showPlaceToast && (
            <div
              className="place-toast"
              dangerouslySetInnerHTML={{
                __html: toastType === 'warning'
                  ? toastMessage
                  : '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle; margin-right: 6px;"><path d="M5.75416 8.8875L13.5229 1.11875C13.7062 0.935417 13.9201 0.84375 14.1646 0.84375C14.409 0.84375 14.6229 0.935417 14.8062 1.11875C14.9896 1.30208 15.0812 1.51994 15.0812 1.77233C15.0812 2.02472 14.9896 2.24228 14.8062 2.425L6.39583 10.8583C6.21249 11.0417 5.9986 11.1333 5.75416 11.1333C5.50971 11.1333 5.29583 11.0417 5.11249 10.8583L1.17083 6.91667C0.987492 6.73333 0.899492 6.51578 0.906826 6.264C0.914159 6.01222 1.0098 5.79436 1.19374 5.61042C1.37769 5.42647 1.59555 5.33481 1.84733 5.33542C2.10033 5.33603 2.32694 5.42958 2.51458 5.61625L5.75416 8.8875Z" fill="#FFD700"/></svg>연동이 완료되었습니다'
              }}
            />
          )}
        </div>
      </div>

      {/* 리뷰 서비스 이름 입력 모달. 화면 하단 약 40% 지점 고정. */}
      {isAddingItem && (
        <div
          className="wp-add-modal-overlay"
          onClick={() => { setIsAddingItem(false); setNewItemName(''); }}
        >
          <div className="wp-add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wp-add-modal-title">리뷰 서비스 메뉴 추가</div>
            <input
              autoFocus
              type="text"
              maxLength={40}
              className="wp-add-modal-input"
              placeholder="예: 사이다 1캔"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddBenefitItem();
                if (e.key === 'Escape') { setIsAddingItem(false); setNewItemName(''); }
              }}
            />
            <div className="wp-add-modal-actions">
              <button
                className="wp-add-modal-btn wp-add-modal-cancel"
                onClick={() => { setIsAddingItem(false); setNewItemName(''); }}
              >취소</button>
              <button
                className="wp-add-modal-btn wp-add-modal-confirm"
                onClick={handleAddBenefitItem}
                disabled={!newItemName.trim()}
              >추가</button>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .review-settings-page {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          background-color: #ffffff;
          z-index: 10000;
          transform: translateX(100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow-y: auto;
          overflow-x: hidden;
        }
        
        .review-settings-page.visible {
          transform: translateX(0);
        }

        .review-settings-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          transform-origin: top center;
          display: flex;
          flex-direction: column;
          position: relative;
        }
        
        @media screen and (min-width: 361px) {
          .review-settings-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .review-settings-wrapper {
            zoom: 1.5;
          }
        }
        
        .page-header {
          width: 360px;
          height: 55px;
          background-color: #ffffff;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          flex-shrink: 0;
        }
        
        .back-button {
          position: absolute;
          left: 16px;
          width: 27px;
          height: 27px;
          border: none;
          background: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }
        
        .page-title-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .review-setting-icon {
          width: 17px;
          height: 17px;
        }
        
        .page-title {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          margin: 0;
        }
        
        /* Tab Bar */
        .tab-bar {
          display: flex;
          width: 360px;
          border-bottom: 1px solid #e0e0e0;
          flex-shrink: 0;
          position: relative;
        }

        .tab-button {
          flex: 1;
          height: 46px;
          border: none;
          background: none;
          font-size: 14px;
          font-weight: 600;
          color: #999;
          cursor: pointer;
          position: relative;
          transition: color 0.2s;
        }

        .tab-button.active {
          color: #333;
        }

        .tab-indicator {
          position: absolute;
          bottom: -1px;
          height: 2px;
          width: 50%;
          background-color: #03C75A;
          border-radius: 1px;
          transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .tab-indicator.left {
          left: 0;
        }

        .tab-indicator.right {
          left: 50%;
        }

        .page-content {
          flex: 1;
          overflow: hidden;
          width: 360px;
          padding: 0;
          box-sizing: border-box;
          min-height: 0;
        }

        .tab-slider {
          display: flex;
          width: 200%;
          height: 100%;
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateX(0);
        }

        .tab-slider.slide-menu {
          transform: translateX(-50%);
        }

        /* 단일 화면(탭 제거): 남은 패널만 전체폭으로 표시 */
        .tab-slider.single {
          width: 100%;
          transform: none;
        }
        .tab-slider.single .tab-panel {
          width: 100%;
        }

        .tab-panel {
          width: 50%;
          flex-shrink: 0;
          padding-bottom: 100px;
          overflow-y: auto;
          scrollbar-width: none;
          height: 100%;
        }

        .tab-panel::-webkit-scrollbar {
          display: none;
        }

        /* Place Search */
        .place-section-title {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          padding: 24px 20px 4px;
        }

        .place-section-desc {
          font-size: 13px;
          color: #888;
          padding: 0 20px 16px;
        }

        .place-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 0;
          font-size: 14px;
          color: #aaa;
        }

        .place-search-bar {
          display: flex;
          gap: 8px;
          padding: 0 20px;
          margin-bottom: 8px;
          box-sizing: border-box;
          width: 100%;
        }

        .place-search-bar .place-search-input {
          min-width: 0;
        }

        .place-search-input {
          flex: 1;
          min-width: 0;
          padding: 14px 16px;
          border: 2px solid #ddd;
          border-radius: 12px;
          font-size: 16px;
          outline: none;
          transition: border-color 0.2s;
        }

        .place-search-input:focus {
          border-color: #03C75A;
        }

        .place-search-btn {
          padding: 14px 24px;
          background: #03C75A;
          color: #fff;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 64px;
        }

        .place-search-btn:active {
          background: #02a84d;
        }

        .place-search-btn:disabled {
          background: #03C75A;
          opacity: 0.7;
        }

        /* Search Results */
        .place-results {
          padding: 0 20px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .place-results::-webkit-scrollbar {
          display: none;
        }

        .place-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          border: 1px solid #e8e8e8;
          border-radius: 12px;
          margin-bottom: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background: #fff;
        }

        .place-card:active {
          background: #f0faf4;
          border-color: #03C75A;
        }

        .place-card.selecting {
          border-color: #03C75A;
          background: #f0faf4;
          pointer-events: none;
        }

        .place-card-info {
          flex: 1;
          min-width: 0;
        }

        .card-spinner {
          width: 20px;
          height: 20px;
          border: 2.5px solid #e0e0e0;
          border-top-color: #03C75A;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
          flex-shrink: 0;
        }

        .btn-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .place-card-name {
          font-size: 15px;
          font-weight: 700;
          color: #222;
          margin-bottom: 4px;
        }

        .place-card-category {
          font-size: 12px;
          color: #03C75A;
          margin-bottom: 4px;
        }

        .place-card-address {
          font-size: 12px;
          color: #888;
        }

        /* Saved Place Card */
        .saved-place-card {
          margin: 16px 20px 0;
          padding: 24px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 0px 8px rgba(0,0,0,0.06);
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.4s ease-out, transform 0.4s ease-out;
        }

        .saved-place-card.show {
          opacity: 1;
          transform: translateY(0);
        }

        .saved-place-name {
          font-size: 22px;
          font-weight: 800;
          color: #222;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #eee;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .saved-place-name-text {
          flex: 1;
        }

        .place-disconnect-btn {
          border: none;
          background: none;
          color: #ccc;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          flex-shrink: 0;
          padding: 2px 0;
          transition: color 0.2s;
        }

        .place-disconnect-btn:active {
          color: #e74c3c;
        }

        .place-toast {
          position: fixed;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          background-color: #3C3C3C;
          color: #ffffff;
          padding: 0 16px;
          height: 42px;
          display: flex;
          align-items: center;
          border-radius: 100px;
          font-size: 14px;
          font-weight: 600;
          z-index: 10001;
          white-space: nowrap;
          animation: placeToastIn 0.3s ease-out, placeToastOut 0.3s ease-out 1.7s forwards;
        }

        @keyframes placeToastIn {
          from { transform: translateX(-50%) translateY(20px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }

        @keyframes placeToastOut {
          from { transform: translateX(-50%) translateY(0); opacity: 1; }
          to { transform: translateX(-50%) translateY(20px); opacity: 0; }
        }

        .live-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #5dd99a;
          flex-shrink: 0;
          position: relative;
        }

        .live-dot::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: #5dd99a;
          transform: translate(-50%, -50%);
          animation: livePulseWave 2.5s ease-out infinite;
        }

        @keyframes livePulseWave {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
          100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
        }

        .stat-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 0;
        }

        .stat-label {
          font-size: 14px;
          font-weight: 600;
          color: #888;
        }

        .stat-value-id {
          font-size: 14px;
          color: #666;
        }

        .stat-value-review {
          font-size: 24px;
          font-weight: 600;
          color: #03C75A;
        }

        .stat-value-time {
          font-size: 13px;
          color: #aaa;
        }

        .place-refresh-btn {
          width: 100%;
          margin-top: 16px;
          padding: 12px;
          background: #f0faf3;
          color: #03C75A;
          border: 1.5px solid #a5d6a7;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s;
        }

        .place-refresh-btn:active {
          background: #e0f2e9;
        }

        .place-refresh-btn:disabled {
          color: #aaa;
          border-color: #ddd;
          background: #fafafa;
        }

        
        .toggle-section {
          margin-top: 30px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
        }
        
        .toggle-label {
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }
        
        .toggle-wrapper {
          display: flex;
          align-items: center;
        }
        
        .toggle-button {
          width: 48px;
          height: 28px;
          background-color: #DEE2E6;
          border: none;
          border-radius: 28px;
          position: relative;
          cursor: pointer;
          transition: background-color 0.3s, transform 0.1s ease;
          padding: 0;
        }

        .toggle-button:active {
          transform: scale(0.9);
        }

        .toggle-button.active {
          background-color: #03C75A;
        }
        .toggle-button.toggle-kakao.active {
          background-color: #FDCD28;
        }
        .review-note-input {
          width: 100%;
          box-sizing: border-box;
          margin-top: 10px;
          border: 1px solid #E5E5E5;
          border-radius: 10px;
          padding: 12px;
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
          min-height: 68px;
          color: #191919;
          outline: none;
        }
        .review-note-input:focus {
          border-color: #FDCD28;
        }

        .toggle-slider {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 24px;
          height: 24px;
          background-color: white;
          border-radius: 50%;
          transition: transform 0.3s;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
        }

        .toggle-button.active .toggle-slider {
          transform: translateX(20px);
        }

        /* Discount Settings Box */
        .discount-settings-box {
          width: 330px;
          margin: 28px auto 0;
          padding: 10px 0;
          border: 1px solid #E4E6E8;
          border-radius: 10px;
          background-color: #ffffff;
          max-height: 400px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          position: relative;
        }
        
        .discount-settings-box::-webkit-scrollbar {
          display: none;
        }

        .empty-message {
          text-align: center;
          padding: 20px;
        }

        .empty-message p {
          margin: 0;
          font-size: 13px;
          font-weight: 400;
          color: #999;
          line-height: 1.5;
        }

        /* Tab 2: Toggle Card */
        .menu-toggle-card {
          margin: 24px 20px 0;
          padding: 20px 22px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 0px 8px rgba(0,0,0,0.06);
        }

        .menu-toggle-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .menu-toggle-divider {
          height: 1px;
          background: #EFEFEF;
          margin: 16px 0;
        }

        .menu-toggle-info {
          flex: 1;
        }

        .menu-toggle-title {
          font-size: 17px;
          font-weight: 800;
          color: #222;
          margin-bottom: 4px;
        }

        .menu-toggle-desc {
          font-size: 13px;
          color: #999;
        }

        /* Tab 2: Menu List Card */
        .menu-list-card {
          margin: 12px 20px 0;
          padding: 22px 22px 20px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 0px 8px rgba(0,0,0,0.06);
        }

        .menu-list-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .menu-list-title2 {
          font-size: 15px;
          font-weight: 700;
          color: #333;
        }

        .menu-list-count {
          font-size: 13px;
          font-weight: 600;
          color: #03C75A;
        }

        .menu-list-items {
        }

        .menu-empty {
          text-align: center;
          padding: 28px 0 0;
        }

        .menu-empty p {
          margin: 0;
          font-size: 12px;
          color: #aaa;
          line-height: 1.6;
        }

        /* Review Menu Card (가로형) */
        .review-menu-card {
          display: flex;
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 14px;
          margin-bottom: 10px;
          height: 90px;
          overflow: hidden;
          user-select: none;
          -webkit-user-select: none;
          will-change: transform;
          transition: box-shadow 0.2s;
          touch-action: pan-y;
          -webkit-touch-callout: none;
        }

        .review-menu-card.dragging {
          border-color: #03C75A;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          transform: scale(1.02);
        }

        .review-menu-image-area {
          width: 90px;
          min-height: 90px;
          flex-shrink: 0;
          background: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .review-menu-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .review-menu-no-image {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: #e8f5e9;
          color: #03C75A;
          font-size: 18px;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .review-menu-info {
          flex: 1;
          padding: 14px 16px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 8px;
          min-width: 0;
        }

        .review-menu-name {
          font-size: 15px;
          font-weight: 700;
          color: #222;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: center;
        }

        .review-menu-edit-btn {
          align-self: center;
          padding: 5px 12px;
          background: #f0faf3;
          color: #03C75A;
          border: 1px solid #c8e6c9;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .review-menu-edit-btn:active {
          background: #e0f2e9;
        }

        .review-menu-hint {
          text-align: center;
          font-size: 12px;
          color: #bbb;
          margin-top: 20px;
        }

        .review-menu-card.deleted {
          opacity: 0.55;
          background: #fafafa;
        }

        .review-menu-card.deleted .review-menu-image-area {
          filter: grayscale(1);
        }

        .deleted-badge {
          display: inline-block;
          margin-left: 6px;
          padding: 2px 7px;
          background: #ffebee;
          color: #e53935;
          border-radius: 5px;
          font-size: 11px;
          font-weight: 700;
          vertical-align: middle;
        }

        .review-menu-remove-btn {
          align-self: center;
          width: 30px;
          height: 30px;
          margin-right: 12px;
          flex-shrink: 0;
          background: transparent;
          border: none;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s;
        }

        .review-menu-remove-btn:active {
          background: #ffebee;
        }

        .review-menu-add-row {
          display: flex;
          justify-content: center;
          margin-top: 30px;
        }

        .review-menu-add-card {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F0FAF3;
          border: 1px solid rgba(3, 199, 90, 0.22);
          border-radius: 12px;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
          transition: background 0.2s, transform 0.1s;
        }

        .review-menu-add-card:active {
          background: #e0f2e9;
          transform: scale(0.94);
        }

        /* 와우포스 — 사은품 추가 모달 (화면 하단 약 40% 지점 고정 — 키보드 등장/사라짐 영향 X) */
        .wp-add-modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.45);
          z-index: 11000;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 20px;
          padding-bottom: 50vh;
        }
        .wp-add-modal {
          width: 100%;
          max-width: 340px;
          background: #fff;
          border-radius: 14px;
          padding: 28px 24px 28px 24px;
          box-shadow: 0 12px 30px rgba(0,0,0,0.25);
        }
        .wp-add-modal-title {
          font-size: 17px;
          font-weight: 700;
          color: #222;
          margin: 0 0 20px 4px;
          text-align: left;
        }
        .wp-add-modal-input {
          width: 100%;
          height: 52px;
          padding: 0 14px;
          border: 1px solid #ddd;
          border-radius: 10px;
          font-size: 15px;
          outline: none;
          box-sizing: border-box;
        }
        .wp-add-modal-input:focus { border: 1.5px solid #03C75A; }
        .wp-add-modal-actions {
          display: flex;
          gap: 10px;
          margin-top: 24px;
        }
        .wp-add-modal-btn {
          flex: 1;
          height: 52px;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
        }
        .wp-add-modal-cancel { background: #f0f0f0; color: #555; }
        .wp-add-modal-confirm { background: #03C75A; color: #fff; }
        .wp-add-modal-confirm:disabled { background: #b6e3c6; cursor: not-allowed; }

        .review-guide-message {
          margin-top: 12px;
          font-size: 13px;
          font-weight: 400;
          color: #999;
          line-height: 1.5;
          text-align: center;
        }

        .menu-name-display {
          flex: 1;
          height: 52px;
          background-color: #FAFAFB;
          border: 1px solid #E8E8E8;
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 16px;
          font-weight: 600;
          color: #282828;
          display: flex;
          align-items: center;
          box-sizing: border-box;
        }

        .discount-item {
          display: flex;
          align-items: center;
          height: 76px;
          padding: 0 22px;
          position: relative;
          background: white;
          user-select: none;
          -webkit-user-select: none;
          will-change: transform;
          overflow: hidden;
        }

        .discount-item.dragging {
          background: white;
          transition: none;
        }
        
        .discount-item.deleting {
          animation: slideOutLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        @keyframes slideOutLeft {
          0% {
            transform: translateX(0);
            opacity: 1;
            height: 62px;
          }
          50% {
            transform: translateX(-100%);
            opacity: 0;
            height: 62px;
          }
          100% {
            transform: translateX(-100%);
            opacity: 0;
            height: 0;
            padding-top: 0;
            padding-bottom: 0;
          }
        }

        .drag-handle-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 14px;
          height: 24px;
          cursor: grab;
          touch-action: none;
          flex-shrink: 0;
          margin-right: 14px;
        }
        
        .drag-handle-wrapper:active {
          cursor: grabbing;
        }

        .drag-handle {
          width: 14px;
          height: 10px;
          pointer-events: none;
        }

        .fixed-bottom {
          background-image: url(${bottomNavBg});
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(50%);
          width: 360px;
          height: 100px;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 0 16px 16px 16px;
          box-sizing: border-box;
          z-index: 100;
          pointer-events: none;
          opacity: 0;
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .fixed-bottom.visible {
          transform: translateX(-50%);
          opacity: 1;
          pointer-events: auto;
        }

        .save-bottom-button {
          width: 100%;
          height: 52px;
          background-color: #03C75A;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 17px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
          pointer-events: auto;
          position: relative;
          z-index: 101;
        }


        .save-bottom-button:active {
          transform: scale(1.02);
          animation: buttonPress 0.2s ease-out;
        }
        
        @keyframes buttonPress {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.03);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </>
  );
};

export default ReviewSettings;